import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useLocalizedLink } from '../../hooks/useLocalizedLink'
import SEO from '../../components/SEO/SEO'
import { seoBreadcrumbPair } from '../../utils/seoUtils'
import './About.css'

function About() {
  const { t, i18n } = useTranslation()
  const getLocalizedLink = useLocalizedLink()
  const isJapanese = i18n.language === 'ja'
  const isRussian = i18n.language === 'ru'

  const breadcrumbItems = useMemo(
    () => seoBreadcrumbPair(t('common.home'), t('common.about'), 'about'),
    [t]
  )

  return (
    <div className="about-page">
      <SEO 
        title={t('about.title')}
        description={t('about.description')}
        breadcrumbItems={breadcrumbItems}
      />
      <div className="container">
        <div className="about-hero">
          <h1>{t('about.title', 'About SUMA International')}</h1>
          <p className="about-intro">{t('about.intro', 'The International School Union of Martial Arts (SUMA International) is a global organization dedicated to the development, preservation, and promotion of traditional and modern martial arts.')}</p>
        </div>

        <section className="about-section">
          <h2>{t('about.mission', 'Our Mission')}</h2>
          <p>{t('about.missionText', 'To unite martial arts schools and federations worldwide, fostering excellence in training, preserving traditional values, and promoting the growth of martial arts disciplines.')}</p>
        </section>

        <section className="about-section">
          <h2>{t('about.values', 'Our Values')}</h2>
          <div className="values-grid">
            <div className="value-card">
              <h3>{t('about.respect', 'Respect')}</h3>
              <p>{t('about.respectText', 'Respect for tradition, teachers, and fellow practitioners')}</p>
            </div>
            <div className="value-card">
              <h3>{t('about.excellence', 'Excellence')}</h3>
              <p>{t('about.excellenceText', 'Commitment to the highest standards of training and practice')}</p>
            </div>
            <div className="value-card">
              <h3>{t('about.unity', 'Unity')}</h3>
              <p>{t('about.unityText', 'Bringing together diverse martial arts communities')}</p>
            </div>
            <div className="value-card">
              <h3>{t('about.heritage', 'Heritage')}</h3>
              <p>{t('about.heritageText', 'Preserving and honoring martial arts traditions')}</p>
            </div>
          </div>
        </section>

        <section className="about-section">
          <h2>{t('about.structure', 'Organization Structure')}</h2>
          <div className="structure-info">
            <p>{t('about.structureText', 'SUMA International is governed by the Supreme Council and its Presidium, with authorized representatives at international, national, regional, and city levels.')}</p>
            <div className="structure-links">
              <Link to={getLocalizedLink('/presidium')} className="structure-link">
                {t('common.presidium')}
              </Link>
              <Link to={getLocalizedLink('/representatives')} className="structure-link">
                {t('common.representatives')}
              </Link>
              <Link to={getLocalizedLink('/federations')} className="structure-link">
                {t('common.federations')}
              </Link>
            </div>
          </div>
        </section>

        <section className="about-section">
          <h2>{t('about.disciplines', 'Martial Arts Disciplines')}</h2>
          <p>{t('about.disciplinesText', 'SUMA International encompasses various martial arts disciplines, each with its own rich history and tradition:')}</p>
          <div className="disciplines-list">
            <Link to={`${getLocalizedLink('/martial-arts')}/kubo`} className="discipline-link">
              {isJapanese ? 'КУБО (古典的ユニバーサルレスリング)' : (isRussian ? 'КУБО (Классическая Универсальная Борьба)' : 'KUBO (Classical Universal Wrestling)')}
            </Link>
            <Link to={`${getLocalizedLink('/martial-arts')}/aikido`} className="discipline-link">
              {isJapanese ? '合気道' : (isRussian ? 'Айкидо' : 'Aikido')}
            </Link>
            <Link to={`${getLocalizedLink('/martial-arts')}/aikijutsu`} className="discipline-link">
              {isJapanese ? '合気術' : (isRussian ? 'Айки-дзюцу' : 'Aiki-jutsu')}
            </Link>
            <Link to={`${getLocalizedLink('/martial-arts')}/jujutsu`} className="discipline-link">
              {isJapanese ? '柔術' : (isRussian ? 'Дзю-дзюцу' : 'Jujutsu')}
            </Link>
            <Link to={`${getLocalizedLink('/martial-arts')}/aikibujutsu`} className="discipline-link">
              {isJapanese ? '合気武術' : (isRussian ? 'Айки-бу-дзюцу' : 'Aiki-bu-jutsu')}
            </Link>
            <Link to={`${getLocalizedLink('/martial-arts')}/karatejutsu`} className="discipline-link">
              {isJapanese ? '空手術' : (isRussian ? 'Карате-дзюцу' : 'Karate-jutsu')}
            </Link>
            <Link to={`${getLocalizedLink('/martial-arts')}/kenjutsu`} className="discipline-link">
              {isJapanese ? '剣術' : (isRussian ? 'Кэн-дзюцу' : 'Kenjutsu')}
            </Link>
            <Link to={`${getLocalizedLink('/martial-arts')}/kempo`} className="discipline-link">
              {isJapanese ? '拳法' : (isRussian ? 'Кемпо' : 'Kempo')}
            </Link>
          </div>
        </section>
      </div>
    </div>
  )
}

export default About
